import { PropsWithChildren, ReactNode } from 'react'
import { FileSpreadsheet } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { buttonVariants } from '@/components/ui/button'
import { gameVersionToSubdomain } from 'app/frontend/types/custom/GameVersion'
import { useTeam } from 'app/frontend/base/config/context/team'
import { usePageInfo } from 'app/frontend/base/config/queries'
import Card from 'app/frontend/base/components/Card'
import CopyableApiKeyValue from 'app/frontend/base/components/CopyableApiKeyValue'
import SheetsUrlInput from 'app/frontend/base/pages/TeamDashboard/subpages/TeamAudit/components/SheetsUrlInput'

interface StepProps {
  number: number
  title: string
  description: ReactNode
}

const Step = ({
  number,
  title,
  description,
  children,
}: PropsWithChildren<StepProps>) => (
  <div className='flex gap-4'>
    <span className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary'>
      {number}
    </span>
    <div className='flex flex-1 flex-col gap-0.5'>
      <span className='text-sm font-medium'>{title}</span>
      <p className='mb-2 text-sm text-muted-foreground'>{description}</p>
      {children}
    </div>
  </div>
)

const SpreadsheetSetupCard = () => {
  const { t } = useTranslation()
  const { team } = useTeam()
  const {
    data: { gameVersion },
  } = usePageInfo()

  return (
    <Card className='flex flex-col gap-6'>
      <h5 className='mb-0 mt-2 flex items-center gap-2'>
        <FileSpreadsheet className='h-5 w-5' />
        Set up spreadsheet
      </h5>

      <p className='mb-2'>
        As an alternative to viewing your team&apos;s data on this site,
        wowaudit also offers almost all data in a Google spreadsheet.
      </p>

      <Step
        number={1}
        title='Get your copy of the spreadsheet'
        description='Create your own copy of the wowaudit spreadsheet to get started.'
      >
        <a
          href={`https://${gameVersionToSubdomain[gameVersion]}wowaudit.com/copy`}
          target='_blank'
          rel='noopener noreferrer'
          className={buttonVariants({ size: 'sm', className: 'self-start' })}
        >
          Make a copy
        </a>
      </Step>

      <Step
        number={2}
        title='Paste your key'
        description='Paste this key in the settings tab of your new spreadsheet.'
      >
        <CopyableApiKeyValue
          value={team.readonlyKey}
          tooltipContent={t('dashboard.team.settings.click-to-copy')}
        />
      </Step>

      <Step
        number={3}
        title='Connect the URL'
        description='Once your copy is ready, paste its docs.google.com URL here to connect it to your team.'
      >
        <SheetsUrlInput />
      </Step>
    </Card>
  )
}

export default SpreadsheetSetupCard
